import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';
import { getOrCreateSystemDeck } from './decks$';
import { createCommentCard, deleteCardsByCommentId } from './cards$';
import { getHighlightById } from './highlights$';
import { getArticleById, getArticleContent } from './articles$';
import { getExtractMarkdownBlocks } from '../utils/extractMarkdownBlocks';
import { getBlockVersion } from './articles$';

// Cache for user info (shared with highlights)
const userInfoCache = new Map();

// Comments store with Supabase sync (local-first)
// No filter - RLS handles permissions (own + shared list comments)
export const comments$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_comments',
  // Custom create/update with error handling (upsert fallback)
  create: createSyncCreate('reedle_comments'),
  update: createSyncUpdate('reedle_comments'),
  // Use default sync config
  ...defaultSyncConfig,
  fieldCreatedAt: 'created_at',
  fieldUpdatedAt: 'updated_at',
  changesSince: 'last-sync',
  // Enable realtime updates
  realtime: true,
  // Persist data locally
  persist: {
    name: 'reedle-comments',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
  // Retry configuration for offline-first
  retry: {
    infinite: true,
    backoff: 'exponential',
    maxDelay: 30000,
  },
}));

// One-time migration: normalize legacy 'block' -> 'paragraph'
// This fixes any local comments created with the old incorrect type
if (typeof window !== 'undefined') {
  // Run after store is initialized
  setTimeout(() => {
    const all = comments$.get() || {};
    Object.values(all).forEach((c) => {
      if (c?.type === 'block') {
        console.log(`Migrating comment ${c.id} from type 'block' to 'paragraph'`);
        comments$[c.id].set({ ...c, type: 'paragraph' });
      }
    });
  }, 100);
}

/**
 * Get user info for a comment owner
 */
export const getCommentOwnerInfo = async (userId) => {
  if (!userId) return null;
  if (userInfoCache.has(userId)) {
    return userInfoCache.get(userId);
  }

  try {
    const { data, error } = await supabase.rpc('get_user_info', { p_user_id: userId });
    if (error || !data || data.length === 0) {
      const info = { username: 'User', avatar_url: null };
      userInfoCache.set(userId, info);
      return info;
    }

    const user = data[0];
    const info = {
      username: user.raw_user_meta_data?.username || user.raw_user_meta_data?.name || user.email?.split('@')[0] || 'User',
      avatar_url: user.raw_user_meta_data?.avatar_url || null,
    };
    userInfoCache.set(userId, info);
    return info;
  } catch (err) {
    console.error('Failed to get user info:', err);
    return { username: 'User', avatar_url: null };
  }
};

/**
 * Check if a comment belongs to current user
 */
export const isOwnComment = (comment) => {
  const currentUserId = auth$.user.get()?.id;
  return comment?.user_id === currentUserId;
};

/**
 * Get all comments for an article
 */
export const getCommentsByArticle = (articleId) => {
  const commentsObj = comments$.get() || {};
  return Object.values(commentsObj)
    .filter(c => c?.article_id === articleId && !c.deleted)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

/**
 * Get article-level comments
 */
export const getArticleComments = (articleId) => {
  return getCommentsByArticle(articleId).filter(c => c.type === 'article');
};

/**
 * Get paragraph comments for a specific block
 */
export const getBlockComments = (articleId, blockIndex) => {
  return getCommentsByArticle(articleId)
    .filter(c => c.type === 'paragraph' && c.block_index === blockIndex);
};

/**
 * Get comments for a specific highlight
 */
export const getHighlightComments = (highlightId) => {
  const commentsObj = comments$.get() || {};
  return Object.values(commentsObj)
    .filter(c => c?.highlight_id === highlightId && !c.deleted)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

/**
 * Get comment counts for an article (for displaying in UI)
 */
export const getCommentCounts = (articleId) => {
  const comments = getCommentsByArticle(articleId);
  const blockCounts = {};
  const highlightCounts = {};
  let articleCount = 0;

  comments.forEach(c => {
    if (c.type === 'article') {
      articleCount++;
    } else if (c.type === 'paragraph' && c.block_index != null) {
      blockCounts[c.block_index] = (blockCounts[c.block_index] || 0) + 1;
    } else if (c.type === 'highlight' && c.highlight_id) {
      highlightCounts[c.highlight_id] = (highlightCounts[c.highlight_id] || 0) + 1;
    }
  });

  return { articleCount, blockCounts, highlightCounts };
};

/**
 * Get the front content for a comment card based on comment type.
 * Async because article content now lives in contentCache (separate IDB).
 * @returns {Promise<string>}
 */
const getCommentFrontContent = async (type, articleId, blockIndex, highlightId) => {
  if (type === 'highlight' && highlightId) {
    const highlight = getHighlightById(highlightId);
    return highlight?.exact_text || '';
  }

  if (type === 'paragraph' && blockIndex !== null) {
    const article = getArticleById(articleId);
    if (!article) return '';
    // Load content from contentCache (article.content is no longer in observable)
    const cached = await getArticleContent(articleId);
    const content = cached?.content;
    if (content) {
      const extractBlocks = getExtractMarkdownBlocks(getBlockVersion(article));
      const blocks = extractBlocks(content);
      return blocks[blockIndex] || '';
    }
  }

  return '';
};

/**
 * Get a comment by its source message index (for AI chat comments)
 * Returns the comment if found, null otherwise
 */
export const getCommentBySourceMessage = (articleId, messageIndex) => {
  if (messageIndex === undefined || messageIndex === null) return null;

  const commentsObj = comments$.get() || {};
  return Object.values(commentsObj).find(c =>
    c?.article_id === articleId &&
    c?.source_message_index === messageIndex &&
    !c.deleted
  ) || null;
};

/**
 * Create a new comment
 * For paragraph/highlight comments, also creates a card in the system Comments deck
 * @param {Object} params
 * @param {string} params.articleId - The article ID
 * @param {string} params.type - Comment type: 'article', 'paragraph', 'highlight'
 * @param {number} [params.blockIndex] - Block index for paragraph comments
 * @param {string} [params.highlightId] - Highlight ID for highlight comments
 * @param {string} params.content - Comment content
 * @param {number} [params.sourceMessageIndex] - AI chat message index (for tracking save state)
 */
export const createComment = async ({ articleId, type, blockIndex, highlightId, content, sourceMessageIndex }) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  // Pre-resolve block_text for paragraph comments so it is stored as a single source of
  // truth in the DB (avoids re-deriving from article content cache later).
  // Done before comment creation so the record is complete from the start.
  let block_text = null;
  if (type === 'paragraph') {
    try {
      block_text = await getCommentFrontContent('paragraph', articleId, blockIndex, null);
    } catch {
      // Non-fatal: block_text stays null; card front will fall back to stored value
    }
  }

  const id = uuidv4();
  const now = new Date().toISOString();
  const newComment = {
    id,
    article_id: articleId,
    user_id: userId,
    type,
    block_index: blockIndex ?? null,
    highlight_id: highlightId ?? null,
    content,
    block_text,
    source_message_index: sourceMessageIndex ?? null,
    created_at: now,
    updated_at: now,
  };

  comments$[id].set(newComment);

  // Auto-generate card for paragraph/highlight comments (not article-level)
  if (type === 'paragraph' || type === 'highlight') {
    try {
      // For paragraph: reuse already-resolved block_text. For highlight: resolve from highlight.
      const front = type === 'paragraph'
        ? block_text
        : await getCommentFrontContent(type, articleId, blockIndex, highlightId);
      if (front && content) {
        const systemDeck = await getOrCreateSystemDeck();
        createCommentCard({
          deckId: systemDeck.id,
          front,
          back: content,
          sourceArticleId: articleId,
          sourceCommentId: id,
          sourceHighlightId: highlightId || null,
        });
      }
    } catch (err) {
      // Don't fail comment creation if card creation fails
      console.error('Failed to create comment card:', err);
    }
  }

  return newComment;
};

/**
 * Update a comment's content
 */
export const updateComment = (commentId, content) => {
  const comment = comments$[commentId].get();
  if (!comment) return null;
  
  comments$[commentId].set({
    ...comment,
    content,
    updated_at: new Date().toISOString(),
  });
  return comments$[commentId].get();
};

/**
 * Delete a comment (hard delete via Legend State)
 * Also deletes any associated card in the Comments deck
 */
export const deleteComment = (commentId) => {
  // First delete any associated card (cascade delete)
  try {
    deleteCardsByCommentId(commentId);
  } catch (err) {
    console.error('Failed to delete comment card:', err);
  }

  comments$[commentId].delete();
};

/**
 * Get total count of comments for current user
 */
export const getTotalCommentCount = () => {
  const currentUserId = auth$.user.get()?.id;
  if (!currentUserId) return 0;

  const commentsObj = comments$.get() || {};
  return Object.values(commentsObj)
    .filter(c => c && !c.deleted && String(c.user_id) === String(currentUserId))
    .length;
};

/**
 * Get all pinned comments for an article
 */
export const getPinnedComments = (articleId) => {
  return getCommentsByArticle(articleId).filter(c => c.is_pinned === true);
};

/**
 * Pin a comment
 */
export const pinComment = (commentId) => {
  const comment = comments$[commentId].get();
  if (!comment) return null;

  comments$[commentId].set({
    ...comment,
    is_pinned: true,
    updated_at: new Date().toISOString(),
  });
  return comments$[commentId].get();
};

/**
 * Unpin a comment
 */
export const unpinComment = (commentId) => {
  const comment = comments$[commentId].get();
  if (!comment) return null;

  comments$[commentId].set({
    ...comment,
    is_pinned: false,
    updated_at: new Date().toISOString(),
  });
  return comments$[commentId].get();
};

/**
 * Toggle pin state of a comment
 */
export const togglePinComment = (commentId) => {
  const comment = comments$[commentId].get();
  if (!comment) return null;

  const newPinState = !comment.is_pinned;
  comments$[commentId].set({
    ...comment,
    is_pinned: newPinState,
    updated_at: new Date().toISOString(),
  });
  return comments$[commentId].get();
};
